import { activities } from "@/config";
import { ReactElement } from "react";
import * as prod from "react/jsx-runtime";
import rehypeExternalLinks from "rehype-external-links";
import rehypeParse from "rehype-parse";
import rehypeReact from "rehype-react";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import rehypeStringify from "rehype-stringify";
import { unified } from "unified";

const production = { Fragment: prod.Fragment, jsx: prod.jsx, jsxs: prod.jsxs };

export function optional<T>(item: T | undefined | null) {
  return item === undefined || item === null ? [] : [item as T];
}

export function shift<T>(array: Array<T>) {
  return array.shift();
}

export function not<T>(func: (arg: T) => boolean) {
  return (item: T) => !func(item);
}

export function split<T>(
  array: Array<T>,
  predicate: (item: T, i: number, array: Array<T>) => boolean
) {
  const left = [];
  const right = [];
  for (let i = 0; i < array.length; i++) {
    if (predicate(array[i], i, array)) {
      right.push(array[i]);
      continue;
    }
    left.push(array[i]);
  }
  return [left, right];
}

export function isDefined<T>(item: T | null | undefined): item is T {
  return item !== undefined && item !== null;
}

export function isEmpty(item: unknown) {
  return (
    item === undefined ||
    item === null ||
    (typeof item === "string" && item === "") ||
    (Array.isArray(item) && item.length === 0) ||
    (typeof item === "object" && Object.keys(item).length === 0)
  );
}

export function stripTags(html: string) {
  return unified()
    .use(rehypeParse, { fragment: true })
    .use(rehypeSanitize, { tagNames: [] })
    .use(rehypeStringify)
    .processSync(html)
    .toString();
}

const richTextTags = [
  "p",
  "br",
  "a",
  "strong",
  "b",
  "em",
  "i",
  "ul",
  "ol",
  "li",
];

export function renderHtmlToReact(
  html: string,
  options?: Partial<{
    allowedTags: string[];
    components: Parameter<typeof rehypeReact, 0>["components"];
  }>
): ReactElement {
  const { allowedTags = richTextTags, components = {} } = options ?? {};
  const { result } = unified()
    .use(rehypeParse, { fragment: true })
    .use(rehypeSanitize, { ...defaultSchema, tagNames: allowedTags })
    .use(rehypeExternalLinks, { rel: ["nofollow"], target: "_blank" })
    .use(rehypeReact, { ...production, components })
    .processSync(html);
  return result;
}

export function truncate(str: string, len: number, delim = "-") {
  const trunc = str.slice(0, len);
  const i = trunc.lastIndexOf(delim);
  return i > -1 ? trunc.slice(0, i) : trunc;
}

export function formatDuration(min: number) {
  const duration = min / 60;
  const hours = Math.floor(duration);
  const minutes = Math.round((duration - hours) * 60);

  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);

  return (
    new Intl.DateTimeFormat("de-DE", {
      hour: "numeric",
      minute: "numeric",
    }).format(date) + " h"
  );
}

export function formatLength(length: number, unit: "meter" | "kilometer") {
  return new Intl.NumberFormat("de-DE", {
    maximumFractionDigits: 1,
    style: "unit",
    unit,
  }).format(length);
}

export function formatUrl(url: string) {
  return new URL(url).hostname;
}

export function formatCoordinatesDMS(coord: Travelguide.GeoCoordinate2D): {
  lat: string;
  lng: string;
} {
  function convert(value: number) {
    const absolute = Math.abs(value);
    const degrees = Math.floor(absolute);
    const minutesNotTruncated = (absolute - degrees) * 60;
    const minutes = Math.floor(minutesNotTruncated);
    const seconds = Math.floor((minutesNotTruncated - minutes) * 60);
    return `${String(degrees).padStart(2, "0")}°${String(minutes).padStart(
      2,
      "0"
    )}'${String(seconds).padStart(2, "0")}"`;
  }
  return {
    lat: `${convert(coord.lat)}${coord.lat < 0 ? "S" : "N"}`,
    lng: `${convert(coord.lng)}${coord.lng < 0 ? "W" : "E"}`,
  };
}

export function findActivityBySlug(
  slug: Travelguide.Activity
): Travelguide.ActivityRoot {
  const activity = (activities as Array<Travelguide.ActivityRoot>).find(
    (item) => item.slug === slug
  );
  if (!activity) {
    throw new Error(`Could not resolve activity type from slug '${slug}'`);
  }
  return activity;
}

export function getActivityUrl(activity: Travelguide.ActivityMap) {
  return `/${activity.slug}`;
}

export function hasProperty<K extends keyof never, P>(propName: K) {
  return <T extends object>(obj: T): obj is T & Record<K, P> => propName in obj;
}

export function byProp<
  P extends string | number | symbol,
  T extends Record<P, unknown>,
>(key: P) {
  return (itemA: T) => (itemB: T) => itemA[key] === itemB[key];
}

export function unique<T>(predicate: (itemA: T) => (itemB: T) => boolean) {
  return (item: T, index: number, array: Array<T>): boolean =>
    array.findIndex(predicate(item)) === index;
}
